// State name → 2-letter abbreviation. Mirrors NAME_TO_ABBR in
// InteractiveStatesMap. Kept inline here so this util has no runtime deps
// on the map component (so tests can import it cheaply).
const NAME_TO_ABBR: Record<string, string> = {
  Alabama: 'AL', Alaska: 'AK', Arizona: 'AZ', Arkansas: 'AR', California: 'CA',
  Colorado: 'CO', Connecticut: 'CT', Delaware: 'DE', 'District of Columbia': 'DC',
  Florida: 'FL', Georgia: 'GA', Hawaii: 'HI', Idaho: 'ID', Illinois: 'IL',
  Indiana: 'IN', Iowa: 'IA', Kansas: 'KS', Kentucky: 'KY', Louisiana: 'LA',
  Maine: 'ME', Maryland: 'MD', Massachusetts: 'MA', Michigan: 'MI', Minnesota: 'MN',
  Mississippi: 'MS', Missouri: 'MO', Montana: 'MT', Nebraska: 'NE', Nevada: 'NV',
  'New Hampshire': 'NH', 'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY',
  'North Carolina': 'NC', 'North Dakota': 'ND', Ohio: 'OH', Oklahoma: 'OK', Oregon: 'OR',
  Pennsylvania: 'PA', 'Puerto Rico': 'PR', 'Rhode Island': 'RI', 'South Carolina': 'SC',
  'South Dakota': 'SD', Tennessee: 'TN', Texas: 'TX', Utah: 'UT', Vermont: 'VT',
  Virginia: 'VA', Washington: 'WA', 'West Virginia': 'WV', Wisconsin: 'WI', Wyoming: 'WY',
};

// Ray-casting point-in-polygon. Each ring is an array of [lng, lat] pairs.
// Standard implementation — counts how many times a horizontal ray from
// (x,y) crosses the polygon's edges; odd = inside.
function pointInRing(x: number, y: number, ring: number[][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    const intersects =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi + 1e-12) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

// Point-in-polygon for a single Polygon (with holes) or MultiPolygon.
function pointInGeometry(lng: number, lat: number, geom: GeoJSON.Geometry): boolean {
  if (geom.type === 'Polygon') {
    const rings = geom.coordinates as number[][][];
    if (rings.length === 0) return false;
    // Outer ring must contain the point; holes (rings 1..N) must not.
    if (!pointInRing(lng, lat, rings[0])) return false;
    for (let i = 1; i < rings.length; i++) {
      if (pointInRing(lng, lat, rings[i])) return false;
    }
    return true;
  }
  if (geom.type === 'MultiPolygon') {
    const polys = geom.coordinates as number[][][][];
    for (const poly of polys) {
      if (poly.length === 0) continue;
      if (!pointInRing(lng, lat, poly[0])) continue;
      let inHole = false;
      for (let i = 1; i < poly.length; i++) {
        if (pointInRing(lng, lat, poly[i])) {
          inHole = true;
          break;
        }
      }
      if (!inHole) return true;
    }
    return false;
  }
  return false;
}

// Given a (lng, lat) and the US states GeoJSON, return the 2-letter state
// code that contains the point, or null if the point falls outside every
// feature (ocean / outside US). Iteration order is the FeatureCollection's
// order; returns the first match.
export function findStateForPoint(
  lng: number,
  lat: number,
  features: GeoJSON.FeatureCollection,
): string | null {
  for (const feature of features.features) {
    if (!feature.geometry) continue;
    if (pointInGeometry(lng, lat, feature.geometry)) {
      const name = (feature.properties as Record<string, string> | null)?.name ?? '';
      return NAME_TO_ABBR[name] ?? null;
    }
  }
  return null;
}
